import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Permission, roleTitle } from '@/types/permission';
import { BusinessUnitSelector } from './BusinessUnitSelector';
import { PeriodSelector, PeriodStatusBadge } from './PeriodSelector';
import { Icon, type IconName } from './Icon';
import { useAuth } from '@/app/AuthContext';
import { useOperationalAccess } from '@/app/useOperationalAccess';
import { v } from '@/theme/tokens';

export function Topbar() {
  const { user, logout, can } = useAuth();
  const operational = useOperationalAccess();
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const initial = (user?.full_name || user?.email || '?').charAt(0).toUpperCase();

  // On a phone the bottom bar holds only Обзор, Компания, Расходы and
  // Производство; the remaining sections live here, each shown only when the
  // session holds the right to it (the server checks again on every request).
  const phoneLinks: { to: string; label: string; icon: IconName }[] = [
    ...(operational.hasAny ? [{ to: '/operational', label: 'Операции', icon: 'clipboard' as IconName }] : []),
    ...(can(Permission.MANAGE_SETTINGS) ? [{ to: '/settings', label: 'Настройки', icon: 'gear' as IconName }] : []),
    ...(can(Permission.VIEW_AUDIT) ? [{ to: '/audit', label: 'Журнал аудита', icon: 'doc' as IconName }] : []),
  ];

  // A menu closes the way every other menu does: Escape (focus goes back to
  // the avatar) or a click anywhere else.
  useEffect(() => {
    if (!menuOpen) return;
    const onPointer = (event: MouseEvent) => {
      if (anchorRef.current && !anchorRef.current.contains(event.target as Node)) setMenuOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setMenuOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  return (
    <header
      className="app-topbar rt-topbar"
      style={{
        height: 64,
        display: 'flex',
        alignItems: 'center',
        padding: '0 24px',
        gap: 14,
        position: 'sticky',
        top: 0,
        zIndex: 20,
      }}
    >
      <BusinessUnitSelector />
      <PeriodSelector />
      {/* The period's draft/confirmed state is a desk decision, not something
          to act on from a phone. Hidden on small screens for room; the status
          itself and the whole confirm workflow are untouched. */}
      <span className="topbar-status">
        <PeriodStatusBadge />
      </span>

      <div style={{ flex: 1, minWidth: 0 }} />

      {/* Uploading lives on Импорты and in the empty state — one place each. */}

      <div ref={anchorRef} style={{ position: 'relative' }}>
        <button
          ref={triggerRef}
          type="button"
          className="rt-avatar"
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="Меню пользователя"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          style={{
            width: 36,
            height: 36,
            borderRadius: 999,
            fontWeight: 700,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          {initial}
        </button>

        {menuOpen && (
          <div
            role="menu"
            aria-label="Меню пользователя"
            className="rt-menu rt-menu--end"
            style={{
              position: 'absolute',
              right: 0,
              top: 44,
              width: 220,
              padding: 6,
              zIndex: 40,
            }}
          >
            <div
              style={{
                padding: '8px 10px',
                borderBottom: `1px solid ${v('line-soft')}`,
                marginBottom: 4,
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 13.5, overflowWrap: 'anywhere' }}>
                {user?.full_name || user?.email}
              </div>
              <div style={{ fontSize: 12, color: v('ink-muted') }}>{roleTitle(user?.role.code)}</div>
            </div>
            {phoneLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                role="menuitem"
                className="rt-menu__item rt-phone-only"
                onClick={() => setMenuOpen(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                  minHeight: 40,
                  padding: '8px 10px',
                  fontSize: 13,
                }}
              >
                <Icon name={link.icon} size={15} />
                {link.label}
              </Link>
            ))}
            <button
              type="button"
              role="menuitem"
              className="rt-menu__item"
              onClick={logout}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 9,
                minHeight: 36,
                padding: '8px 10px',
                fontSize: 13,
              }}
            >
              <Icon name="logout" size={15} />
              Выйти
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
